import React, {useState, useCallback, useEffect} from 'react';
import MyMap from "./components/Map";
import LayerSelector from "./components/LayerSelector";
import InfoPanel from "./components/InfoPanel";
import QueryOptionForm from "./components/QueryOptionForm";
import ResultPanel from "./components/ResultPanel";
import Login from "./components/Login";
import Logout from "./components/Logout";

// if there is an env var API_URL then we use it, otherwise we use localhost api address
// remember to add "REACT_APP_" prefix
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5050";

function App() {
    const [mapState, setMapState] = useState({
        longitude: 13.051408,
        latitude: 47.59303396770133,
        zoom: 8
    })
    const [selectedLayerValue, setSelectedLayerValue] = useState('1');
    const [selectedFeatures, setSelectedFeatures] = useState([]);
    const [selectedOptions, setSelectedOptions] = useState(['gemeinde', 'worker', 'in']);
    const [resultFeatureData, setResultFeatureData] = useState({
        type: 'FeatureCollection',
        features: [{}]
    })
    const [resultSummaryValues, setResultSummaryValues] = useState([0, 0]);

    const [popupInfo, setPopupInfo] = useState({
        coordinates: {},
        featureName: "",
    });

    const [ifShowExport, setIfShowExport] = useState(false);
    const [queryUnit, setQueryUnit] = useState("gemeinde");
    // assign negative value initially to hide the legend in the beginning
    const [naturalBreaks, setBreaks] = useState([-1,0]);

    // if autenticated, set ifProtected is not null
    const [ifProtected,setIfProtected] = useState(false);
    const [userName, setUserName] = useState("");

    const checkAuth = useCallback(async() => {
        try {
            const response = await fetch(`./auth/protected`, {
                method: "GET",
                credentials: "include"
            });
            const data = await response.json();
            console.log(data);
            setUserName(data.user);
            if (response.ok) {
                setIfProtected(true);
            } else {
                setIfProtected(false);
            }
            // Remove any query string "?" after successful login, else it will be shown in the url
            window.history.replaceState({}, document.title, window.location.pathname);
        } catch (error) {   
            console.log(error);
            setIfProtected(false);
        }
    }, []);

    const handleLogout = useCallback(async () => {
        try {
            const response = await fetch(`./auth/logout`, {
                method: "POST",
                credentials: "include" // This ensures the cookie is sent
            });

            if (response.ok) {
                alert("Logout erfolgreich!");
                setIfProtected(false);
                setUserName(""); 
            } else {
                alert("Logout fehlgeschlagen!");
            }
        } catch (error) {
            alert("Logout fehlgeschlagen!");
        }
    }, []);


    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const handleSelectFeature = useCallback(evt =>{
        const newFeature = evt.features[0];

        if (!checkIfAlreadySelected(newFeature, selectedFeatures)){
            setSelectedFeatures([newFeature, ...selectedFeatures]);
        } else {
            // remove the feature from the array if it is clicked on again in the map
            setSelectedFeatures(selectedFeatures.filter((f) => f.properties["spec_id"] !== newFeature.properties["spec_id"] ))
        }
        // check if the new selected feature is already in the selected feature array
        function checkIfAlreadySelected(new_feature, feature_arr ){
                const feature_spec_id_arr = feature_arr.map(e => e.properties["spec_id"]);
                return feature_spec_id_arr.includes(new_feature.properties["spec_id"]);
        }
    }, [selectedFeatures])

    const handleDelete = ()=>{
        setSelectedOptions(['gemeinde', 'worker', 'in']);
        setIfShowExport(false);
        setSelectedFeatures([]);
        setResultFeatureData({type: 'FeatureCollection', features: [{}]});
        setQueryUnit('gemeinde');
        setSelectedLayerValue('1');
        setBreaks([-1,0]);
    }

    const handleSelectLayer =  useCallback(evt=>{
        let target_value = evt.currentTarget.value;
        setSelectedLayerValue(target_value);
        if (target_value === '1'){
            setQueryUnit( "gemeinde");
        } else if (target_value === '2'){
            setQueryUnit ( '1000m');
        } else if (target_value==='3') {
            setQueryUnit('500m');
        }
        // remove previous selected units when layer type changes
        setSelectedFeatures([]);
    }, []);

    // const handleNextStep = ()=>{
    //     alert("Data querying...")
    // }

    const handleQuerySubmit = useCallback((evt) => {
        evt.preventDefault();
        // get the list of the spec_id of selected features
        let spec_id_list = selectedFeatures.map(e => e.properties["spec_id"]);
        let commuter_type = selectedOptions[1];
        let direction = selectedOptions[2];
        let result_unit = selectedOptions[0];
        let query_objects = spec_id_list.toString();

        Promise.all([
            // get mapping units, react automatically know it is from the public directory
            fetch("./data/einheiten-4326-2021.geojson").then(res => res.json()),
            // query commuters and corresponding feature ids
            //fetch(`${API_URL}/query/${commuter_type}/${direction}/${queryUnit}/${result_unit}/${query_objects}`)
            fetch(`./query/${commuter_type}/${direction}/${queryUnit}/${result_unit}/${query_objects}`)
                .then((response) => response.json())
        ]).then(([allFeatures, commuterResult]) => {
            // console.log(commuterResult);
            let commuterResultEntries = commuterResult.entries;
            let result_specIDs = commuterResultEntries.map(e => e[0]);
            // filter map units by their specid and add its matching commuter number to its property
            let filteredFeatures = allFeatures.features.filter(item => {
                let item_spec_id = item.properties.spec_id;
                let matching_commuterResult = commuterResultEntries.find((e) => e[0] === item_spec_id);
                if (result_specIDs.filter(filteredFeature => filteredFeature === item_spec_id).length > 0) {
                    item.properties["commuter_value"] = matching_commuterResult[1];
                    return item;
                }
            })

            let resultUnits = filteredFeatures.length;
            let totalCommuter = filteredFeatures.reduce((total, e)=>{ return total + e.properties["commuter_value"]}, 0);
            setBreaks(commuterResult.jnb_breaks);
            setResultSummaryValues([totalCommuter, resultUnits]);

            // create new geojson based on the query result to be visualized later
            let resultFeatureCollection = {
                type: 'FeatureCollection',
                features: filteredFeatures
            }
            setIfShowExport(true);
            setResultFeatureData(resultFeatureCollection);
            setSelectedLayerValue('4');
        })
            .catch((error) => {
                console.log(error)
            })
    }, [selectedFeatures, selectedOptions])

    const handleQueryReset = useCallback((evt)=>{
        evt.preventDefault();
        setSelectedFeatures([]);
        setIfShowExport(false);
        setSelectedOptions(['gemeinde', 'worker', 'in']);
        setSelectedLayerValue('1');
        setQueryUnit('gemeinde');
        setResultFeatureData({type: 'FeatureCollection', features: [{}]});
        setBreaks([-1,0]);
    }, [])

    const handleSetupPopup = useCallback((evt)=>{
        evt.originalEvent.stopPropagation();
        // if there is feature where the mouse is, then set up the popup
        if (evt.features[0]) {
            try {
                setPopupInfo({
                    coordinates: evt.lngLat,
                    featureName: evt.features[0].properties.name
                })
            } catch (err) {
                console.log(err)
            }
        } else {
            setPopupInfo({
                    coordinates:{} ,
                     featureName: "",
                })
        }
    }, [selectedLayerValue])

    const formatQueryResultTexts = (selectedOptions, ifMultipleUnits) => {
        let formatedResultTexts = {
            commuterType: "",
            direction:"",
            unitType:"",
        }
        formatedResultTexts.commuterType = selectedOptions[1] === "student"? "Schülerpendler":"Erwerbspendler";
        formatedResultTexts.direction = selectedOptions[2]==="in"? "aus": "in";
        let resultUnitType = selectedOptions[0];
        switch (resultUnitType){
            case "1000m":
                if (ifMultipleUnits){
                    formatedResultTexts.unitType = "1000m Rasterzellen";
                } else {
                    formatedResultTexts.unitType = "1000m Rasterzelle";
                }
                break;
            case "500m":
                if (ifMultipleUnits){
                    formatedResultTexts.unitType = "500m Rasterzellen"
                } else {
                    formatedResultTexts.unitType = "500m Rasterzelle";
                }
                break;
            default:
                if (ifMultipleUnits){
                    formatedResultTexts.unitType = "Gemeinden"
                } else {
                    formatedResultTexts.unitType = "Gemeinde"
                }

        }
        return formatedResultTexts
    }

    return (
        <>
        {ifProtected? 
        <div className="App">
            <Logout handleLogout={handleLogout} userName={userName}/>
            <LayerSelector
                selectedLayerValue={selectedLayerValue}
                selectLayer={handleSelectLayer}
            />
            {selectedFeatures.length > 0 &&
                <InfoPanel
                    features={selectedFeatures}
                    deleteSelected={handleDelete}
                    // nextStep={handleNextStep}
                    queryOptions={selectedOptions}
                    submitQuery={handleQuerySubmit}
                />}
            {selectedFeatures.length > 0 &&
                <QueryOptionForm
                    options={selectedOptions}
                    setOptions={setSelectedOptions}
                    handleSubmit={handleQuerySubmit}
                    handleReset={handleQueryReset}
                    ifShowExportButton={ifShowExport}
                />}
            {JSON.stringify(resultFeatureData.features[0]) !== "{}" &&
                <ResultPanel
                    commuterNum = {resultSummaryValues[0]}
                    commuterType = {formatQueryResultTexts(selectedOptions).commuterType}
                    direction={formatQueryResultTexts(selectedOptions).direction}
                    unitNum={resultSummaryValues[1]}
                    unitType={resultSummaryValues[1]>1? formatQueryResultTexts(selectedOptions, true).unitType : formatQueryResultTexts(selectedOptions, false).unitType }
                />}
            <MyMap
                mapState={mapState}
                setMapState={setMapState}
                visibleLayer={selectedLayerValue}
                features={selectedFeatures}
                selectFeature={handleSelectFeature}
                resultFeatures={resultFeatureData}
                setupPopup={handleSetupPopup}
                popupInfo={popupInfo}
                valueBreaks={naturalBreaks}
            />
        </div>: <Login onLogin={checkAuth}/>}
        </>
    );
}

export default App;
